/**
 * AuthContext — Firebase auth with THREE providers (Phone, Email Link, Google).
 *
 * Sign-in flows:
 *   Phone:  sendOtp(e164) → verifyOtp(code, trustDevice)
 *   Email:  sendEmailLink(email) → user clicks link → verifyEmailLinkFromUrl()
 *   Google: signInWithGoogleProvider(trustDevice)
 *
 * All three complete the same way:
 *   Firebase returns an ID token → POST /api/auth/firebase/session → app JWT.
 *
 * Device Trust:
 *   `trustDevice=true` yields a 30-day signed JWT persisted in localStorage.
 *   On boot, if there's a device_token, we call `/api/auth/device-signin` and skip OTP entirely.
 *
 * Sign-out:
 *   Fully signs out of Firebase (`fbSignOut`) + revokes device trusts server-side +
 *   clears both `sp_token` and `sp_device_token` from localStorage.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { http } from "@/lib/api";
import {
  firebaseSignOut,
  sendPhoneOtp,
  sendEmailMagicLink,
  isEmailLinkCallback,
  readStoredEmailForLink,
  completeEmailLinkSignIn,
  signInWithGoogle,
} from "@/lib/firebase";

const AuthContext = createContext(null);
const TOKEN_KEY = "sp_token";
const DEVICE_KEY = "sp_device_token";

function readStored(key) {
  try { return localStorage.getItem(key); } catch { return null; }
}
function writeStored(key, val) {
  try { val ? localStorage.setItem(key, val) : localStorage.removeItem(key); } catch { /* ignore */ }
}

/**
 * Best-effort local decode of a JWT payload. Used only to detect staff
 * magic-link sessions so we can skip the /auth/me call (which requires
 * a real users doc). Returns null on any parse error; the token is still
 * validated server-side on every API call.
 */
function decodeJwtPayload(token) {
  try {
    const seg = token.split(".")[1];
    if (!seg) return null;
    const padded = seg.replace(/-/g, "+").replace(/_/g, "/");
    const pad    = padded.length % 4 ? "=".repeat(4 - (padded.length % 4)) : "";
    return JSON.parse(atob(padded + pad));
  } catch { return null; }
}

// Prime the axios instance's default Authorization header from whatever's stored on boot.
(function bootstrapAuthHeader() {
  const t = readStored(TOKEN_KEY);
  if (t) http.defaults.headers.common.Authorization = `Bearer ${t}`;
})();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const confirmationRef = useRef(null);

  const applyToken = useCallback((token) => {
    if (token) {
      writeStored(TOKEN_KEY, token);
      http.defaults.headers.common.Authorization = `Bearer ${token}`;
    } else {
      writeStored(TOKEN_KEY, null);
      delete http.defaults.headers.common.Authorization;
    }
  }, []);

  const refresh = useCallback(async () => {
    // Staff magic-link tokens don't back a real users doc — hydrate locally
    // from the JWT payload instead of calling /auth/me (which would 401).
    const existing = readStored(TOKEN_KEY);
    if (existing) {
      const payload = decodeJwtPayload(existing);
      const notExpired = payload?.exp && payload.exp * 1000 > Date.now();
      if (payload?.role === "staff" && notExpired) {
        setUser({
          id: payload.id || `staff-${payload.email}`,
          email: payload.email,
          name: payload.name || `Staff · ${(payload.email || "").split("@")[0]}`,
          role: "staff",
          business_id: payload.business_id,
          business_name: payload.business_name,
        });
        setLoading(false);
        return;
      }
    }
    // Priority: existing Bearer token → /auth/me. If that fails, try device-signin.
    try {
      const { data } = await http.get("/auth/me");
      setUser(data.user || data);   // legacy endpoint returns user directly
    } catch (err) {
      // 5xx (Cloudflare 520, pod restart, transient backend outage)
      // is NOT a real anonymous state — keep the user signed in and
      // let the next call retry. Only treat 401/403 as "actually
      // signed out". Otherwise a single 520 quietly logs everyone out.
      const status = err?.response?.status;
      if (status && status >= 500) {
        setLoading(false);
        return;   // preserve current user + token, quietly retry later
      }
      const deviceToken = readStored(DEVICE_KEY);
      if (deviceToken) {
        try {
          const { data } = await http.post("/auth/device-signin", { device_token: deviceToken });
          applyToken(data.token);
          setUser(data.user);
        } catch {
          writeStored(DEVICE_KEY, null);
          applyToken(null);
          setUser(null);
        }
      } else {
        applyToken(null);
        setUser(null);
      }
    } finally {
      setLoading(false);
    }
  }, [applyToken]);

  useEffect(() => { refresh(); }, [refresh]);

  const sendOtp = useCallback(async (phoneE164) => {
    const result = await sendPhoneOtp(phoneE164);
    confirmationRef.current = result;
    return result;
  }, []);

  const _exchangeIdToken = useCallback(async (idToken, trustDevice, extras = {}) => {
    // India-user extras — passed through to /api/auth/firebase/session so
    // the backend region-detector can score correctly at first signup.
    // On repeat sign-ins these are ignored server-side.
    const { data } = await http.post("/auth/firebase/session", {
      id_token: idToken,
      trust_device: !!trustDevice,
      device_label: typeof navigator !== "undefined" ? navigator.userAgent.slice(0, 80) : null,
      declared_country: extras.declared_country || null,
      dpdp_consent: !!extras.dpdp_consent,
    });
    applyToken(data.token);
    writeStored(DEVICE_KEY, data.device_token?.token || null);
    setUser(data.user);
    // We don't need the Firebase session on the client any more — clear it so future
    // sign-ins always start fresh (per product spec).
    await firebaseSignOut();
    confirmationRef.current = null;
    return { user: data.user, is_new_user: !!data.is_new_user };
  }, [applyToken]);

  const verifyOtp = useCallback(async (code, trustDevice = false, extras = {}) => {
    if (!confirmationRef.current) throw new Error("Please request an OTP first.");
    const cred = await confirmationRef.current.confirm(code);
    const idToken = await cred.user.getIdToken(true);
    return await _exchangeIdToken(idToken, trustDevice, extras);
  }, [_exchangeIdToken]);

  const sendEmailLink = useCallback(async (email) => {
    await sendEmailMagicLink(email);
  }, []);

  const verifyEmailLinkFromUrl = useCallback(async (emailOverride, trustDevice = false, extras = {}) => {
    if (!isEmailLinkCallback()) throw new Error("This URL isn't a sign-in link.");
    const email = (emailOverride || readStoredEmailForLink() || "").trim().toLowerCase();
    if (!email) throw new Error("Please enter the email you used to request the link.");
    const cred = await completeEmailLinkSignIn(email);
    const idToken = await cred.user.getIdToken(true);
    return await _exchangeIdToken(idToken, trustDevice, extras);
  }, [_exchangeIdToken]);

  const signInWithGoogleProvider = useCallback(async (trustDevice = false, extras = {}) => {
    const cred = await signInWithGoogle();
    const idToken = await cred.user.getIdToken(true);
    return await _exchangeIdToken(idToken, trustDevice, extras);
  }, [_exchangeIdToken]);

  /**
   * Magic-link staff sign-in — no Firebase involvement. Accepts the app-issued
   * JWT + a lightweight staff user descriptor and hydrates the auth state so
   * navbars, guards etc. update immediately without a page reload.
   */
  const setSessionFromMagicLink = useCallback((appToken, staffUser) => {
    applyToken(appToken);
    // Clear any device-trust token from a previous full-account session — staff
    // sessions are short-lived and scoped to a single business.
    writeStored(DEVICE_KEY, null);
    setUser(staffUser || null);
    setLoading(false);
  }, [applyToken]);

  const logout = useCallback(async () => {
    // 1) Revoke device trusts server-side.
    try { await http.post("/auth/logout"); } catch { /* ignore */ }
    // 2) Fully sign out of Firebase (clears its device-local tokens).
    await firebaseSignOut();
    // 3) Wipe our own tokens.
    applyToken(null);
    writeStored(DEVICE_KEY, null);
    setUser(null);
  }, [applyToken]);

  const value = useMemo(
    () => ({
      user, loading,
      sendOtp, verifyOtp,
      sendEmailLink, verifyEmailLinkFromUrl,
      signInWithGoogleProvider,
      setSessionFromMagicLink,
      logout, refresh,
    }),
    [user, loading, sendOtp, verifyOtp, sendEmailLink, verifyEmailLinkFromUrl, signInWithGoogleProvider, setSessionFromMagicLink, logout, refresh],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
